/**
 * Single source of truth for project status and health.
 *
 * Status = lifecycle stage (where the project is in the workflow)
 * Health = risk indicator (how it's going — only set for in-progress statuses)
 */

export type ProjectStatus =
  | "requested"
  | "approved"
  | "functional_spec"
  | "technical_spec"
  | "ready_for_development"
  | "in_progress"
  | "testing_go_live"
  | "hypercare"
  | "complete"
  | "postponed"
  | "declined";

// N/A is an explicit, user-selectable health value for cases where the
// risk indicator doesn't apply yet (a not-started project that hasn't
// been assessed) or where the user wants to suppress green/yellow/red
// without leaving the field blank. Status DOES NOT override health —
// PMO sets it manually, including yellow on a pre-start project that
// has a looming kickoff risk.
export type ProjectHealth = "green" | "yellow" | "red" | "n_a";

export interface StatusMeta {
  key: ProjectStatus;
  label: string;            // Full label (e.g. "Functional Spec")
  shortLabel: string;       // Short label for narrow columns (e.g. "Func Spec")
  pillClass: string;        // Tailwind classes for status pill
  order: number;            // For sorting / display order
}

// Status pill color rule (Brett, sitewide):
//   - Not in progress (pre-work or terminal-not-done) → light gray (slate)
//   - In progress (anything actively in the workflow) → light blue (sky)
//   - Done / Complete                                  → emerald
// Health (green/yellow/red) and Priority (rose/amber/sky) keep their own
// palettes — those are separate signals.
export const STATUS_OPTIONS: StatusMeta[] = [
  { key: "requested",        label: "Requested",         shortLabel: "Requested",  pillClass: "bg-raised text-ink-2",     order: 0 },
  { key: "approved",         label: "Approved",          shortLabel: "Approved",   pillClass: "bg-raised text-ink-2",     order: 2 },
  { key: "functional_spec",  label: "Functional Spec",   shortLabel: "Func Spec",  pillClass: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",         order: 3 },
  { key: "technical_spec",   label: "Technical Spec",    shortLabel: "Tech Spec",  pillClass: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",         order: 4 },
  { key: "ready_for_development", label: "Ready for Development", shortLabel: "Ready for Dev", pillClass: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",   order: 5 },
  { key: "in_progress",         label: "In Progress",       shortLabel: "In Progress", pillClass: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",        order: 6 },
  { key: "testing_go_live",  label: "Testing / Go Live", shortLabel: "Testing",    pillClass: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",         order: 7 },
  // Hypercare = go-live done, still on post-go-live support. Indigo so
  // it reads as a distinct late stage — NOT "In Progress" (sky) and NOT
  // "Complete" (emerald). A project here is active but not done.
  { key: "hypercare",        label: "Hypercare",         shortLabel: "Hypercare",  pillClass: "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",   order: 8 },
  { key: "complete",         label: "Complete",          shortLabel: "Complete",   pillClass: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", order: 9 },
  // Key stays "postponed" (no data migration); display label is
  // "On Hold" — Brett's preferred term for the paused-but-could-
  // resume state, and it already showed as On Hold in short form.
  { key: "postponed",        label: "On Hold",           shortLabel: "On Hold",    pillClass: "bg-raised text-ink-muted",     order: 10 },
  { key: "declined",         label: "Declined",          shortLabel: "Declined",   pillClass: "bg-raised text-ink-muted",     order: 11 },
];

export const STATUS_BY_KEY: Record<ProjectStatus, StatusMeta> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.key, s]),
) as Record<ProjectStatus, StatusMeta>;

export const HEALTH_OPTIONS: Array<{ key: ProjectHealth; label: string; dotClass: string; pillClass: string; textClass: string }> = [
  { key: "green",  label: "Green",  dotClass: "bg-emerald-500", pillClass: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", textClass: "text-emerald-700 dark:text-emerald-300" },
  { key: "yellow", label: "Yellow", dotClass: "bg-amber-500",   pillClass: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",     textClass: "text-amber-700 dark:text-amber-300" },
  { key: "red",    label: "Red",    dotClass: "bg-red-500",     pillClass: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",         textClass: "text-red-700 dark:text-red-300" },
  { key: "n_a",    label: "N/A",    dotClass: "bg-muted-strong",   pillClass: "bg-raised text-ink-muted",     textClass: "text-ink-muted" },
];

export const HEALTH_BY_KEY: Record<ProjectHealth, (typeof HEALTH_OPTIONS)[number]> = Object.fromEntries(
  HEALTH_OPTIONS.map((h) => [h.key, h]),
) as Record<ProjectHealth, (typeof HEALTH_OPTIONS)[number]>;

// Statuses where work is actively underway (project is not done).
// Hypercare counts — go-live is done but the team is still on support.
export const IN_PROGRESS_STATUSES: ProjectStatus[] = [
  "functional_spec",
  "technical_spec",
  "in_progress",
  "testing_go_live",
  "hypercare",
];

export const TERMINAL_STATUSES: ProjectStatus[] = ["complete", "postponed", "declined"];
export const PRE_START_STATUSES: ProjectStatus[] = ["requested", "approved", "ready_for_development"];

export function isInProgress(status: string | null | undefined): boolean {
  return !!status && (IN_PROGRESS_STATUSES as string[]).includes(status);
}

export function isTerminal(status: string | null | undefined): boolean {
  return !!status && (TERMINAL_STATUSES as string[]).includes(status);
}

export function isAtRisk(health: string | null | undefined): boolean {
  if (!health) return false;
  const h = health.toLowerCase();
  return h === "yellow" || h === "red";
}

export function isNeedsHelp(health: string | null | undefined): boolean {
  return (health || "").toLowerCase() === "red";
}

/** Returns label for a status, or a humanized version of the raw string if unknown. */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return "—";
  const meta = STATUS_BY_KEY[status as ProjectStatus];
  if (meta) return meta.label;
  // Fallback: humanize snake_case
  return status.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function statusShortLabel(status: string | null | undefined): string {
  if (!status) return "—";
  const meta = STATUS_BY_KEY[status as ProjectStatus];
  return meta?.shortLabel ?? statusLabel(status);
}

export function statusPillClass(status: string | null | undefined): string {
  if (!status) return "bg-raised text-ink-muted";
  return STATUS_BY_KEY[status as ProjectStatus]?.pillClass ?? "bg-raised text-ink-muted";
}

export function healthLabel(health: string | null | undefined): string {
  if (!health) return "—";
  const meta = HEALTH_BY_KEY[health.toLowerCase() as ProjectHealth];
  return meta?.label ?? health;
}

export function healthPillClass(health: string | null | undefined): string {
  if (!health) return "bg-raised text-ink-muted";
  return HEALTH_BY_KEY[health.toLowerCase() as ProjectHealth]?.pillClass ?? "bg-raised text-ink-muted";
}

export function healthDotClass(health: string | null | undefined): string {
  if (!health) return "bg-muted-strong";
  return HEALTH_BY_KEY[health.toLowerCase() as ProjectHealth]?.dotClass ?? "bg-muted-strong";
}

// ---------------------------------------------------------------------------
// Effective health — single helper every render site passes through to
// turn the raw health field into a label + pill class. Status is in the
// signature for symmetry with future logic but DOES NOT override what
// the user picked: a not-started project explicitly set to Yellow stays
// Yellow on the page. Empty / unknown values render as N/A in slate.
// `isAssessed` is true when the row has a real green/yellow/red — useful
// for tally/filter logic that should skip N/A.
// ---------------------------------------------------------------------------

export interface EffectiveHealth {
  label: string;        // "Green" | "Yellow" | "Red" | "N/A"
  pillClass: string;
  dotClass: string;
  textClass: string;
  isAssessed: boolean;  // false when health resolves to N/A
}

// Health not assessed (unset, or explicitly N/A). Neutral dash —
// same visual language as DONE_DISPLAY: "no risk signal here."
const NA_DISPLAY: EffectiveHealth = {
  label: "—",
  pillClass: "bg-raised text-ink-muted",
  dotClass: "bg-muted-strong",
  textClass: "text-ink-muted",
  isAssessed: false,
};

// Completed work — health (a forward-looking RISK indicator) no
// longer applies. Render a neutral dash instead of a stale
// green/yellow/red. The stored health value is untouched in the DB;
// this only changes display.
const DONE_DISPLAY: EffectiveHealth = {
  label: "—",
  pillClass: "bg-raised text-ink-subtle",
  dotClass: "bg-muted-strong",
  textClass: "text-ink-subtle",
  isAssessed: false,
};

export function effectiveHealth(
  status: string | null | undefined,
  health: string | null | undefined,
): EffectiveHealth {
  // Once an item is complete, health is not a meaningful signal —
  // there's no forward risk to assess. Show "—" regardless of the
  // stored health value. Applies to both projects and initiatives.
  const s = (status ?? "").toLowerCase().trim();
  if (s === "complete" || s === "completed" || s === "done") {
    return DONE_DISPLAY;
  }
  const h = (health ?? "").toLowerCase().trim();
  // Treat null/empty/legacy variants of "not applicable" as N/A.
  if (!h || h === "n_a" || h === "na" || h === "n/a") return NA_DISPLAY;
  const meta = HEALTH_BY_KEY[h as ProjectHealth];
  if (!meta || meta.key === "n_a") return NA_DISPLAY;
  return {
    label: meta.label,
    pillClass: meta.pillClass,
    dotClass: meta.dotClass,
    textClass: meta.textClass,
    isAssessed: true,
  };
}

/** Tabs/sections for grouping projects on list pages.
 *  `in_flight` is a multi-status FILTER tab — it spans spec/build/testing
 *  so a single click from Executive's "In-Flight" KPI lands on every
 *  active project at once. The single-status tabs below it still work
 *  for narrower views; first-match bucketing in `portfolioTabFor` keeps
 *  projects classified by their specific status, not "in_flight". */
export const PORTFOLIO_TABS: Array<{ key: string; label: string; statuses: ProjectStatus[] }> = [
  { key: "requested",      label: "Requested",      statuses: ["requested"] },
  { key: "approved",       label: "Approved",       statuses: ["approved"] },
  { key: "in_flight",      label: "All In-Flight",  statuses: ["functional_spec", "technical_spec", "in_progress", "testing_go_live", "hypercare"] },
  { key: "in_spec",        label: "In Spec",        statuses: ["functional_spec", "technical_spec"] },
  { key: "ready_for_development", label: "Ready for Dev", statuses: ["ready_for_development"] },
  { key: "in_progress",       label: "In Progress",    statuses: ["in_progress"] },
  { key: "testing",        label: "Testing / GL",   statuses: ["testing_go_live"] },
  { key: "hypercare",      label: "Hypercare",      statuses: ["hypercare"] },
  { key: "complete",       label: "Complete",       statuses: ["complete"] },
  // On Hold and Declined were one shared tab; split 2026-08-11 (Brett) —
  // "paused, may resume" and "we said no" are different answers, and
  // declined requests were invisible under an On Hold label.
  { key: "on_hold",        label: "On Hold",        statuses: ["postponed"] },
  { key: "declined",       label: "Declined",       statuses: ["declined"] },
];

/** Filter tabs span multiple statuses — they're views, not categories.
 *  Excluded from `portfolioTabFor` first-match bucketing so projects
 *  classify by their specific status. */
const PORTFOLIO_FILTER_TABS = new Set<string>(["in_flight"]);

/** Return the category tab key a project belongs to. */
export function portfolioTabFor(status: string | null | undefined): string {
  if (!status) return "approved";
  for (const tab of PORTFOLIO_TABS) {
    if (PORTFOLIO_FILTER_TABS.has(tab.key)) continue;
    if ((tab.statuses as string[]).includes(status)) return tab.key;
  }
  return "approved";
}
